/*
  Execution, wave 3 (screen 09 idea 7): Table | Board | Matrix.
  - ExecutionViewSwitch — the three views (kept in the URL as ?ex.view=)
  - ExecutionBoard      — main tasks as cards in status columns (foundation <Board />): drag, keyboard or the
                          card's "Move to…" menu changes the status like the table's status cell (an unsaved edit:
                          the count, the leave prompt and "Save execution" work the same)
  - ExecutionMatrix     — buildings × tasks heatmap (foundation <MatrixGrid />): each cell the % of the task's
                          items in that building that are Done, or ticked on site; a click opens the table on that
                          building with the task open
*/
import { useEffect, useMemo, useRef, useState } from 'react';
import { Grid3x3, SquareKanban, Table2 } from 'lucide-react';
import { Board } from '@/components/ui/board';
import { MatrixGrid } from '@/components/ui/matrix-grid';
import { Segmented } from '@/components/ui/segmented';
import { ProgressBar } from '@/components/ui/progress-bar';
import { StatusPill } from '@/components/ui/status-pill';
import { Avatar } from '@/components/ui/avatar-initials';
import { formatNumber } from '@/lib/format';
import { BOARD_COLUMNS, boardColumnOf, buildExecutionMatrix, taskProgress } from './siteModel';
import { splitPeople } from './executionModel';

export const EXECUTION_VIEWS = [
  { key: 'table', label: 'Table', icon: Table2 },
  { key: 'board', label: 'Board', icon: SquareKanban },
  { key: 'matrix', label: 'Matrix', icon: Grid3x3 },
];

export function ExecutionViewSwitch({ value, onChange }) {
  return (
    <Segmented
      ariaLabel="Execution view"
      value={value}
      onChange={onChange}
      size="sm"
      options={EXECUTION_VIEWS.map((view) => {
        const Icon = view.icon;
        return {
          key: view.key,
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {view.label}
            </span>
          ),
          testId: `execution-view-${view.key}`,
        };
      })}
      testId="execution-view"
    />
  );
}

function BoardCard({ task, tree }) {
  const progress = taskProgress(task, tree);
  const people = splitPeople(task.done_by);
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-[13.5px] font-semibold leading-snug text-ink">{task.title}</p>
        {task.status === 'Update required' ? <StatusPill status="Update required" domain="execution" size="sm" /> : null}
      </div>
      {progress.total ? (
        <div className="flex items-center gap-2">
          <ProgressBar value={(progress.done / progress.total) * 100} label={`${task.title} done`} className="flex-1" />
          <span className="text-[11.5px] font-semibold tabular-nums text-ink-3">{formatNumber(progress.done, { decimals: 0 })}/{formatNumber(progress.total, { decimals: 0 })}</span>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 text-[12px] text-ink-3">
        <span className="truncate">{task.category || 'No category'}</span>
        {people.length ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Avatar name={people[0]} size={20} />
            <span className="truncate text-ink-2">{people[0]}{people.length > 1 ? ` +${people.length - 1}` : ''}</span>
          </span>
        ) : <span>Unassigned</span>}
      </div>
    </div>
  );
}

const BOARD_GAP = 12;
const BOARD_MIN_COLUMN = 168;
const BOARD_MAX_COLUMN = 272;

/** Column width that fits all status columns in the box (09 #7: six columns at 1440 / 1366), never below 168px. */
function useBoardColumnWidth(count) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const fit = width ? Math.floor((width - BOARD_GAP * (count - 1)) / count) : 248;
  return [ref, Math.max(BOARD_MIN_COLUMN, Math.min(BOARD_MAX_COLUMN, fit))];
}

export function ExecutionBoard({ tasks = [], tree, canEdit, onMove, onOpen }) {
  const mains = useMemo(() => tasks.filter((task) => !task.parent_id), [tasks]);
  const [boxRef, columnWidth] = useBoardColumnWidth(BOARD_COLUMNS.length);
  return (
    <div ref={boxRef} className="min-w-0">
    <Board
      ariaLabel="Execution tasks by status"
      columns={BOARD_COLUMNS.map((column) => ({ ...column, testId: `execution-board-${column.key.replace(/\s+/g, '-').toLowerCase()}` }))}
      items={mains}
      getKey={(task) => task.id}
      getColumn={boardColumnOf}
      getCardLabel={(task) => task.title}
      renderCard={(task) => <BoardCard task={task} tree={tree} />}
      onMove={canEdit ? async (task, to) => { await onMove(task, to); } : undefined}
      canMove={(task, to) => boardColumnOf(task) !== to}
      onOpen={onOpen}
      columnWidth={columnWidth}
      emptyText="No tasks"
      testId="execution-board"
    />
    </div>
  );
}

export function ExecutionMatrix({ tasks = [], tree, metric = 'done', onMetricChange, onCellClick }) {
  const matrix = useMemo(() => buildExecutionMatrix(tasks, tree, metric), [tasks, tree, metric]);
  if (!matrix.rows.length) {
    return (
      <div className="rounded-card border border-line bg-surface px-5 py-8 text-center text-[13px] text-ink-3 shadow-card" data-testid="execution-matrix-empty">
        The matrix needs tasks that run per building — add buildings in Project Structure.
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-card border border-line bg-surface p-4 shadow-card" data-testid="execution-matrix">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-[14px] font-bold text-ink">Buildings × tasks</h3>
          <p className="text-[12.5px] text-ink-3">
            % of each task&apos;s items in the building that are {metric === 'checked' ? 'ticked on site (Checked by)' : 'Done'} · click a cell to open them
          </p>
        </div>
        <Segmented
          ariaLabel="Matrix shows"
          size="sm"
          value={metric}
          onChange={onMetricChange}
          options={[{ key: 'done', label: 'Done', testId: 'execution-matrix-done' }, { key: 'checked', label: 'Checked', testId: 'execution-matrix-checked' }]}
        />
      </div>
      <MatrixGrid
        ariaLabel={`Execution ${metric === 'checked' ? 'checked on site' : 'done'} by building and task`}
        rowHeader="Building"
        rows={matrix.rows}
        columns={matrix.columns.map(({ key, label, title }) => ({ key, label, title }))}
        getCell={(row, column) => matrix.cell(row.key, column.key)}
        formatValue={(value) => `${value}`}
        legend
        legendLabels={['0%', '100%']}
        onCellClick={(row, column) => onCellClick?.(row.key, matrix.columns.find((entry) => entry.key === column.key)?.task)}
        cellWidth={76}
        testId="execution-matrix-grid"
      />
    </div>
  );
}
